import { cn } from "@/lib/utils";
import type { ExampleVizBlock } from "../../dGrid.type";

interface ExampleVizProps {
  blocks: ExampleVizBlock[];
  label?: string;
}

const renderBlock = (block: ExampleVizBlock, index: number) => {
  switch (block.kind) {
    case "calc":
      return (
        <div
          key={index}
          className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1.5 text-[15px] font-semibold"
        >
          {block.text}
          <em className="text-[22px] font-extrabold text-primary not-italic">
            {block.result}
          </em>
        </div>
      );
    case "bar":
      return (
        <div
          key={index}
          className="flex h-[34px] overflow-hidden rounded-lg bg-white"
        >
          {/* Runtime width: the share of the bill being illustrated. */}
          <i
            className="block bg-primary"
            style={{ width: `${block.percent}%` }}
          />
        </div>
      );
    case "ticket":
      return (
        <div
          key={index}
          className="relative rounded-xl border-l-[3px] border-dashed border-primary bg-white py-4 pr-5 pl-7 before:absolute before:-top-2.5 before:-left-3 before:size-5 before:rounded-full before:bg-paper after:absolute after:-bottom-2.5 after:-left-3 after:size-5 after:rounded-full after:bg-paper"
        >
          <b className="block text-[30px] leading-[1.1] font-extrabold tracking-[-0.02em] text-primary">
            {block.value}
          </b>
          <span className="text-sm text-ink-muted">{block.caption}</span>
        </div>
      );
    case "week":
      // A small calendar strip: active (offer) days are solid red pills
      // with white text, the rest muted, each with a status dot.
      return (
        <div key={index} className="rounded-xl bg-white p-1.5 shadow-card">
          <div className="grid grid-cols-7 gap-1">
            {block.days.map((day) => {
              const isActive = block.activeDays.includes(day);
              return (
                <span
                  key={day}
                  className={cn(
                    "flex h-12 flex-col items-center justify-center gap-1 rounded-lg text-[11px] font-bold tracking-wide uppercase",
                    isActive ? "bg-primary text-white" : "text-ink-muted",
                  )}
                >
                  {day}
                  <span
                    aria-hidden="true"
                    className={cn(
                      "size-1.5 rounded-full",
                      isActive ? "bg-white" : "bg-line",
                    )}
                  />
                </span>
              );
            })}
          </div>
        </div>
      );
    case "rule":
      return (
        <p key={index} className="text-sm font-semibold text-ink">
          {block.text}
        </p>
      );
    case "bubble":
      return (
        <p
          key={index}
          className="rounded-[14px_14px_14px_4px] bg-white px-3.5 py-3 text-[14.5px] leading-[1.45] shadow-xs"
        >
          {block.text}
        </p>
      );
  }
};

// The paper panel inside an example card that pictures the example: a
// calculation, a coupon, a week strip, a chat bubble...
const ExampleViz = ({ blocks, label }: ExampleVizProps) => (
  <div
    role={label ? "img" : undefined}
    aria-label={label}
    className="flex min-h-[150px] flex-col justify-center gap-3 rounded-2xl bg-paper p-5"
  >
    {blocks.map(renderBlock)}
  </div>
);

export default ExampleViz;
